<template>
    <v-container class="py-7">
        <v-row>
            <v-col :cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">Leilões</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
                            { text: 'Início', disabled: false, to: '/home', exact: true },
                            { text: 'Leilões', disabled: true, exact: true },
                        ]"></v-breadcrumbs>
                    </div>
                    <div class="button-group">
                        <v-btn type="button" color="primary" elevation="0" @click="openForm()">Incluir</v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card class="table-card">
                    <v-card-title class="table-card-title">Leilões publicados no site</v-card-title>
                    <v-card-subtitle>Os próximos leilões aparecem na página "Leilões"; os realizados nos últimos 12 meses
                        ficam listados abaixo deles.</v-card-subtitle>
                    <v-card-text>
                        <v-data-table :headers="headers" :items="items" :loading="loading" :disable-sort="true"
                            :items-per-page="20"
                            :footer-props="{ itemsPerPageText: 'Itens por página:', itemsPerPageOptions: [20, 50, -1], itemsPerPageAllText: 'Todos', pageText: '{0}-{1} de {2}' }"
                            no-data-text="Nenhum leilão cadastrado"
                            loading-text="Carregando dados..." class="app-table hover-table">
                            <template v-slot:[`item.auction_at`]="{ item }">
                                {{ item.auction_at | defaultDate("DD/MM/YYYY HH:mm") }}
                            </template>
                            <template v-slot:[`item.client`]="{ item }">{{ item.client ? item.client.name : '—' }}</template>
                            <template v-slot:[`item.actions`]="{ item }">
                                <v-row align="center" no-gutters>
                                    <v-icon @click="openForm(item)" size="large" class="me-2">mdi-pencil-outline</v-icon>
                                    <v-icon @click="deleteItem(item)" size="large">mdi-delete</v-icon>
                                </v-row>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="form.show" max-width="640" persistent>
            <v-card>
                <v-card-title>{{ form.data.id ? 'Editar leilão' : 'Incluir leilão' }}</v-card-title>
                <v-card-text>
                    <v-form ref="form">
                        <v-text-field dense outlined label="Título (bem leiloado)" v-model="form.data.title"
                            :rules="[$rules.required]" hide-details="auto" class="mb-4"></v-text-field>
                        <v-row>
                            <v-col cols="7">
                                <v-menu v-model="form.menuDate" :close-on-content-click="false" offset-y min-width="auto">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field dense outlined readonly label="Data" v-bind="attrs" v-on="on"
                                            :value="form.date | defaultDate('DD/MM/YYYY')" :rules="[$rules.required]"
                                            hide-details="auto"></v-text-field>
                                    </template>
                                    <v-date-picker v-model="form.date" no-title locale="pt-br"
                                        @input="form.menuDate = false"></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="5">
                                <v-text-field dense outlined label="Hora" type="time" v-model="form.time"
                                    :rules="[$rules.required]" hide-details="auto"></v-text-field>
                            </v-col>
                        </v-row>
                        <v-text-field dense outlined label="Leiloeiro" v-model="form.data.auctioneer"
                            :rules="[$rules.required]" hide-details="auto" class="my-4"></v-text-field>
                        <v-text-field dense outlined label="Link do leilão" v-model="form.data.auction_url"
                            placeholder="https://..." :rules="[$rules.required, httpsUrl]" hide-details="auto"
                            class="mb-4"></v-text-field>
                        <v-autocomplete dense outlined clearable label="Caso (opcional)" v-model="form.data.client_id"
                            :items="clients" item-value="id" item-text="name" hide-details="auto"
                            class="mb-4"></v-autocomplete>
                        <v-textarea dense outlined label="Descrição (opcional)" v-model="form.data.description" rows="3"
                            auto-grow hide-details="auto"></v-textarea>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="form.show = false" :disabled="form.saving">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" @click="save" :loading="form.saving">Salvar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
    name: "AuctionList",

    data: () => ({
        loading: false,
        items: [],
        clients: [],
        headers: [
            { text: "Data", value: "auction_at", width: 160 },
            { text: "Título", value: "title" },
            { text: "Caso", value: "client" },
            { text: "Leiloeiro", value: "auctioneer" },
            { text: "Ações", value: "actions", sortable: false, width: 100 },
        ],
        form: { show: false, saving: false, menuDate: false, date: null, time: "", data: {} },
    }),

    created() {
        this.load();
        this.loadClients();
    },

    methods: {
        httpsUrl(value) {
            return !value || /^https:\/\/\S+$/i.test(value.trim()) || "Use um link começando com https://";
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get("/api/admin/auctions");
                this.items = result.data.content;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },

        async loadClients() {
            try {
                const result = await axios.get("/api/clients", { params: { limit: 100000 } });
                this.clients = (result.data.content.data || []).sort((a, b) => a.name.localeCompare(b.name));
            } catch (error) {
                this.clients = [];
            }
        },

        openForm(item) {
            const auctionAt = item ? moment(item.auction_at) : null;
            this.form.data = item ? { ...item } : { title: "", auctioneer: "", auction_url: "", description: "", client_id: null };
            this.form.date = auctionAt ? auctionAt.format("YYYY-MM-DD") : null;
            this.form.time = auctionAt ? auctionAt.format("HH:mm") : "";
            this.form.show = true;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },

        async save() {
            if (!this.$refs.form.validate()) return;

            // Data e hora digitadas no fuso de quem cadastra; a API recebe com o fuso explícito.
            const payload = {
                title: this.form.data.title,
                auction_at: moment(`${this.form.date} ${this.form.time}`, "YYYY-MM-DD HH:mm").format(),
                auctioneer: this.form.data.auctioneer,
                auction_url: this.form.data.auction_url.trim(),
                client_id: this.form.data.client_id || null,
                description: this.form.data.description,
            };

            this.form.saving = true;
            try {
                if (this.form.data.id) await axios.patch(`/api/admin/auctions/${this.form.data.id}`, payload);
                else await axios.post("/api/admin/auctions", payload);

                this.$dialog.notify.info("Leilão salvo", { position: "top-right", timeout: 2000 });
                this.form.show = false;
                this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.form.saving = false;
            }
        },

        async deleteItem(item) {
            const proceedAction = await this.$dialog.confirm({
                text: `Excluir o leilão "${item.title}"?`,
                title: "Excluir leilão",
                actions: { false: "Cancelar", true: "Excluir" },
            });

            if (!proceedAction) return;

            try {
                await axios.delete(`/api/admin/auctions/${item.id}`);
                this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            }
        },
    },
};
</script>
